import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true
});

export const requestPasswordReset = async (email) => {
  const response = await apiClient.post('/auth/forgot-password', { email });
  return response.data;
};

export const resetPassword = async (token, newPassword) => {
  const response = await apiClient.post('/auth/reset-password', {
    token,
    new_password: newPassword
  });
  return response.data;
};

// Dashboard
export const getDashboard = async () => {
  const response = await apiClient.get('/portal/dashboard');
  return response.data;
};

// Projects
export const getProjects = async () => {
  const response = await apiClient.get('/portal/projects');
  return response.data;
};

export const getProject = async (projectId) => {
  const response = await apiClient.get(`/portal/projects/${projectId}`);
  return response.data;
};

export const createProject = async (projectData) => {
  const response = await apiClient.post('/portal/projects', projectData);
  return response.data;
};

export const updateProject = async (projectId, projectData) => {
  const response = await apiClient.put(`/portal/projects/${projectId}`, projectData);
  return response.data;
};

export const deleteProject = async (projectId) => {
  const response = await apiClient.delete(`/portal/projects/${projectId}`);
  return response.data;
};

export const getProjectJobs = async (projectId) => {
  const response = await apiClient.get(`/portal/projects/${projectId}/jobs`);
  return response.data;
};

// Users (Admin)
export const getUsers = async () => {
  const response = await apiClient.get('/portal/users');
  return response.data;
};

export const createUser = async (userData) => {
  const response = await apiClient.post('/portal/users', userData);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await apiClient.put(`/portal/users/${userId}`, userData);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await apiClient.delete(`/portal/users/${userId}`);
  return response.data;
};

// Jobs
export const getJobs = async () => {
  const response = await apiClient.get('/portal/jobs');
  return response.data;
};

export const getJob = async (jobId) => {
  const response = await apiClient.get(`/portal/jobs/${jobId}`);
  return response.data;
};

export const createJob = async (jobData) => {
  const response = await apiClient.post('/portal/jobs', jobData);
  return response.data;
};

export const createMultipleJobs = async (projectId, clientId, workTypes) => {
  const response = await apiClient.post(
    `/portal/jobs/bulk?project_id=${projectId}&client_id=${clientId}`,
    workTypes
  );
  return response.data;
};

export const updateJob = async (jobId, jobData) => {
  const response = await apiClient.put(`/portal/jobs/${jobId}`, jobData);
  return response.data;
};

export const deleteJob = async (jobId) => {
  const response = await apiClient.delete(`/portal/jobs/${jobId}`);
  return response.data;
};

// Documents
export const getDocuments = async ({ category = null, jobId = null, projectId = null } = {}) => {
  let url = '/portal/documents';
  const params = new URLSearchParams();
  if (category) params.append('category', category);
  if (jobId) params.append('job_id', jobId);
  if (projectId) params.append('project_id', projectId);
  if (params.toString()) url += `?${params.toString()}`;
  
  const response = await apiClient.get(url);
  return response.data;
};

export const uploadDocument = async (file, clientId, projectId = null, jobId = null, category = 'cits', visibleToClient = true) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('client_id', clientId);
  if (projectId) formData.append('project_id', projectId);
  if (jobId) formData.append('job_id', jobId);
  formData.append('category', category);
  formData.append('visible_to_client', visibleToClient.toString());
  
  const response = await apiClient.post('/portal/documents', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const deleteDocument = async (docId) => {
  const response = await apiClient.delete(`/portal/documents/${docId}`);
  return response.data;
};

export const downloadDocument = async (docId, filename) => {
  const response = await fetch(`${BACKEND_URL}/api/portal/documents/${docId}/download`, {
    credentials: 'include'
  });
  
  if (!response.ok) throw new Error('Download failed');
  
  const blob = await response.blob();
  const blobUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(blobUrl);
};

// Messages
export const getMessages = async () => {
  const response = await apiClient.get('/portal/messages');
  return response.data;
};

export const sendMessage = async (subject, content, recipientId = null) => {
  const response = await apiClient.post('/portal/messages', {
    subject,
    content,
    recipient_id: recipientId
  });
  return response.data;
};

export const markMessageRead = async (msgId) => {
  const response = await apiClient.put(`/portal/messages/${msgId}/read`, {});
  return response.data;
};

// Contact
export const submitContact = async (subject, message) => {
  const response = await apiClient.post('/portal/contact', {
    subject,
    message
  });
  return response.data;
};

// Public price request
export const submitPriceRequest = async (payload) => {
  const response = await apiClient.post('/public/price-request', payload);
  return response.data;
};

// Testimonials
export const submitTestimonial = async (payload) => {
  const response = await apiClient.post('/public/testimonials', payload);
  return response.data;
};

export const getPublicTestimonials = async () => {
  const response = await apiClient.get('/public/testimonials');
  return response.data;
};

export const getAdminTestimonials = async (status = null) => {
  const query = status ? `?status_filter=${status}` : '';
  const response = await apiClient.get(`/admin/testimonials${query}`);
  return response.data;
};

export const updateTestimonialStatus = async (testimonialId, status) => {
  const response = await apiClient.put(`/admin/testimonials/${testimonialId}`, { status });
  return response.data;
};

export const getVisitorStats = async (days = 30) => {
  const response = await apiClient.get(`/portal/admin/visitors/stats?days=${days}`);
  return response.data;
};

export const getRecentVisits = async (limit = 20) => {
  const response = await apiClient.get(`/portal/admin/visitors/recent?limit=${limit}`);
  return response.data;
};

export const logVisit = async (payload) => {
  const response = await apiClient.post('/portal/visits', payload);
  return response.data;
};

// Audit Logs (Admin)
export const getAuditLogs = async () => {
  const response = await apiClient.get('/portal/audit-logs');
  return response.data;
};

export const getEmployees = async () => {
  const response = await apiClient.get('/portal/employees');
  return response.data;
};

export const createEmployee = async (payload) => {
  const response = await apiClient.post('/portal/employees', payload);
  return response.data;
};

export const updateEmployee = async (employeeId, payload) => {
  const response = await apiClient.put(`/portal/employees/${employeeId}`, payload);
  return response.data;
};

export const deleteEmployee = async (employeeId) => {
  const response = await apiClient.delete(`/portal/employees/${employeeId}`);
  return response.data;
};

// Employee Tasks
export const getEmployeeTasks = async (params = {}) => {
  const response = await apiClient.get('/portal/employee-tasks', { params });
  return response.data;
};

export const createEmployeeTask = async (payload) => {
  const response = await apiClient.post('/portal/employee-tasks', payload);
  return response.data;
};

export const updateEmployeeTask = async (taskId, payload) => {
  const response = await apiClient.put(`/portal/employee-tasks/${taskId}`, payload);
  return response.data;
};


export const updateEmployeeTaskStatus = async (taskId, payload) => {
  const response = await apiClient.put(`/portal/employee-tasks/${taskId}/status`, payload);
  return response.data;
};

export const deleteEmployeeTask = async (taskId) => {
  const response = await apiClient.delete(`/portal/employee-tasks/${taskId}`);
  return response.data;
};

export const getEmployeeTask = async (taskId) => {
  const response = await apiClient.get(`/portal/employee-tasks/${taskId}`);
  return response.data;
};

export const getEmployeeTaskReports = async (taskId) => {
  const response = await apiClient.get(`/portal/employee-tasks/${taskId}/reports`);
  return response.data;
};

export const deleteEmployeeReportExpense = async (reportId, expenseId) => {
  const response = await apiClient.delete(`/portal/employee-tasks/reports/${reportId}/expenses/${expenseId}`);
  return response.data;
};

export const getMyEmployeeTasks = async () => {
  const response = await apiClient.get('/portal/employee-tasks/mine');
  return response.data;
};

export const createEmployeeReport = async (taskId, payload) => {
  const response = await apiClient.post(`/portal/employee-tasks/${taskId}/reports`, payload);
  return response.data;
};

export const uploadReceipt = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/portal/employee-tasks/receipts', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

export const getReceipt = async (receiptId) => {
  const response = await apiClient.get(`/portal/employee-tasks/receipts/${receiptId}`, {
    responseType: 'blob'
  });
  return response.data;
};

// Work tracking
export const getTaskWorkTypes = async () => {
  const response = await apiClient.get('/portal/employee-tasks/work-types');
  return response.data;
};

export const generateTaskLink = async (taskId) => {
  const response = await apiClient.post(`/portal/employee-tasks/${taskId}/link`);
  return response.data;
};

export const sendTaskLink = async (taskId, payload) => {
  const response = await apiClient.post(`/portal/employee-tasks/${taskId}/send-link`, payload);
  return response.data;
};

// Project boundaries (KML / GeoJSON)
export const getProjectBoundaries = async () => {
  const response = await apiClient.get('/portal/projects/boundaries');
  return response.data;
};

export const getProjectBoundary = async (projectId) => {
  const response = await apiClient.get(`/portal/projects/${projectId}/boundary`);
  return response.data;
};

export const uploadProjectBoundary = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`/portal/projects/${projectId}/boundary`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const deleteProjectBoundary = async (projectId) => {
  const response = await apiClient.delete(`/portal/projects/${projectId}/boundary`);
  return response.data;
};

// Felling areas (cirsmas)
export const getAllFellingAreas = async () => {
  const response = await apiClient.get('/portal/felling-areas');
  return response.data;
};

export const getProjectFellingAreas = async (projectId) => {
  const response = await apiClient.get(`/portal/projects/${projectId}/felling-areas`);
  return response.data;
};

export const createFellingArea = async (projectId, payload) => {
  const response = await apiClient.post(`/portal/projects/${projectId}/felling-areas`, payload);
  return response.data;
};

export const bulkCreateFellingAreas = async (projectId, payload) => {
  const response = await apiClient.post(`/portal/projects/${projectId}/felling-areas/bulk`, payload);
  return response.data;
};

export const importFellingAreas = async (projectId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post(`/portal/projects/${projectId}/felling-areas/import`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const updateFellingArea = async (areaId, payload) => {
  const response = await apiClient.put(`/portal/felling-areas/${areaId}`, payload);
  return response.data;
};

export const deleteFellingArea = async (areaId) => {
  const response = await apiClient.delete(`/portal/felling-areas/${areaId}`);
  return response.data;
};

export const exportProjectMap = async (projectId, format = 'kml') => {
  const response = await apiClient.get(`/portal/projects/${projectId}/export.${format}`, { responseType: 'blob' });
  return response.data;
};

export const getEmployeeTaskMap = async (taskId) => {
  const response = await apiClient.get(`/portal/employee-tasks/${taskId}/map`);
  return response.data;
};

export const lookupVmdAffirmation = async (number) => {
  const response = await apiClient.get(`/portal/vmd/affirmation/${encodeURIComponent(number)}`);
  return response.data;
};

export const getMapLayers = async () => {
  const response = await apiClient.get('/portal/settings/map-layers');
  return response.data;
};

export const updateMapLayers = async (layers) => {
  const response = await apiClient.put('/portal/settings/map-layers', { layers });
  return response.data;
};

export const fetchWmsCapabilities = async (url) => {
  const response = await apiClient.get('/portal/map/wms-capabilities', { params: { url } });
  return response.data;
};

export const parseWmsCapabilitiesXml = async (xml) => {
  const response = await apiClient.post('/portal/map/wms-capabilities/parse', { xml });
  return response.data;
};

// Next-step templates (admin)
export const getNextStepTemplates = async () => {
  const response = await apiClient.get('/portal/settings/next-step-templates');
  return response.data;
};

export const updateNextStepTemplates = async (templates) => {
  const response = await apiClient.put('/portal/settings/next-step-templates', { templates });
  return response.data;
};

// Notifications
export const getNotifications = async (limit = 30) => {
  const response = await apiClient.get('/portal/notifications', { params: { limit } });
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await apiClient.put(`/portal/notifications/${notificationId}/read`);
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await apiClient.put('/portal/notifications/read-all');
  return response.data;
};

export const getTaskByCode = async (code) => {
  const response = await apiClient.get(`/portal/employee-tasks/by-code/${code}`);
  return response.data;
};

export const getTaskFiles = async (taskId) => {
  const response = await apiClient.get(`/portal/employee-tasks/${taskId}/files`);
  return response.data;
};

export const getWorkEntries = async (params = {}) => {
  const response = await apiClient.get('/portal/work-entries', { params });
  return response.data;
};

export const getMyWorkEntries = async (params = {}) => {
  const response = await apiClient.get('/portal/work-entries/mine', { params });
  return response.data;
};

export const createWorkEntries = async (entries, source = 'manual') => {
  const response = await apiClient.post('/portal/work-entries', { entries, source });
  return response.data;
};

export const updateMyWorkEntry = async (entryId, payload) => {
  const response = await apiClient.put(`/portal/work-entries/mine/${entryId}`, payload);
  return response.data;
};

export const updateWorkEntry = async (entryId, payload) => {
  const response = await apiClient.put(`/portal/work-entries/${entryId}`, payload);
  return response.data;
};

export const deleteWorkEntry = async (entryId) => {
  const response = await apiClient.delete(`/portal/work-entries/${entryId}`);
  return response.data;
};

export const exportWorkEntries = async (params = {}) => {
  const response = await apiClient.get('/portal/work-entries/export', { params, responseType: 'blob' });
  return response.data;
};

export const aiParseWorkReport = async (payload) => {
  const response = await apiClient.post('/portal/work-entries/ai-parse', payload);
  return response.data;
};

export const getMileage = async (params = {}) => {
  const response = await apiClient.get('/portal/mileage', { params });
  return response.data;
};

export const createMileage = async (payload) => {
  const response = await apiClient.post('/portal/mileage', payload);
  return response.data;
};

export const updateMileage = async (entryId, payload) => {
  const response = await apiClient.put(`/portal/mileage/${entryId}`, payload);
  return response.data;
};

export const deleteMileage = async (entryId) => {
  const response = await apiClient.delete(`/portal/mileage/${entryId}`);
  return response.data;
};

export const getPayrollAdjustments = async (params = {}) => {
  const response = await apiClient.get('/portal/payroll-adjustments', { params });
  return response.data;
};

export const createPayrollAdjustment = async (payload) => {
  const response = await apiClient.post('/portal/payroll-adjustments', payload);
  return response.data;
};

export const deletePayrollAdjustment = async (entryId) => {
  const response = await apiClient.delete(`/portal/payroll-adjustments/${entryId}`);
  return response.data;
};

export const getSalary = async (params) => {
  const response = await apiClient.get('/portal/salary', { params });
  return response.data;
};

export const getClosedMonths = async (params = {}) => {
  const response = await apiClient.get('/portal/salary/months', { params });
  return response.data;
};

export const addManualSalaryEntry = async (payload) => {
  const response = await apiClient.post('/portal/salary/manual-entry', payload);
  return response.data;
};

export const verifySalaryMonth = async (payload) => {
  const response = await apiClient.post('/portal/salary/verify', payload);
  return response.data;
};

export const paySalaryMonth = async (payload) => {
  const response = await apiClient.post('/portal/salary/pay', payload);
  return response.data;
};

export const reopenSalaryMonth = async (payload) => {
  const response = await apiClient.post('/portal/salary/reopen', payload);
  return response.data;
};

export const exportSalary = async (params) => {
  const response = await apiClient.get('/portal/salary/export', { params, responseType: 'blob' });
  return response.data;
};

export const getProjectProgress = async (projectId) => {
  const response = await apiClient.get(`/portal/projects/${projectId}/progress`);
  return response.data;
};

export const exportSalaryPdf = async (params) => {
  const response = await apiClient.get('/portal/salary/export-pdf', { params, responseType: 'blob' });
  return response.data;
};

export const sendSalaryPdf = async (payload) => {
  const response = await apiClient.post('/portal/salary/send-pdf', payload);
  return response.data;
};

export const exportProjectReportPdf = async (projectId, internal = false) => {
  const response = await apiClient.get(`/portal/projects/${projectId}/report-pdf`, { params: { internal }, responseType: 'blob' });
  return response.data;
};

export const sendProjectReport = async (projectId, payload) => {
  const response = await apiClient.post(`/portal/projects/${projectId}/send-report`, payload);
  return response.data;
};

export const getProjectFinance = async (projectId) => {
  const response = await apiClient.get(`/portal/projects/${projectId}/finance`);
  return response.data;
};

export const updateProjectFinance = async (projectId, payload) => {
  const response = await apiClient.put(`/portal/projects/${projectId}/finance`, payload);
  return response.data;
};

export const addProjectCost = async (projectId, payload) => {
  const response = await apiClient.post(`/portal/projects/${projectId}/costs`, payload);
  return response.data;
};

export const deleteProjectCost = async (projectId, costId) => {
  const response = await apiClient.delete(`/portal/projects/${projectId}/costs/${costId}`);
  return response.data;
};

export const getProjectsFinance = async () => {
  const response = await apiClient.get('/portal/admin/projects-finance');
  return response.data;
};

export const getTaxSettings = async (year) => {
  const response = await apiClient.get('/portal/tax-settings', { params: year ? { year } : {} });
  return response.data;
};

export const getTaxSettingsYears = async () => {
  const response = await apiClient.get('/portal/tax-settings/years');
  return response.data;
};

export const updateTaxSettings = async (payload) => {
  const response = await apiClient.put('/portal/tax-settings', payload);
  return response.data;
};

export const getAdminWarnings = async () => {
  const response = await apiClient.get('/portal/admin/warnings');
  return response.data;
};

export const getAdminOverview = async () => {
  const response = await apiClient.get('/portal/admin/overview');
  return response.data;
};

export const getEmployeeOverview = async () => {
  const response = await apiClient.get('/portal/employee/overview');
  return response.data;
};

export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Finance
export const getFinanceEntries = async () => {
  const response = await apiClient.get('/finance/entries');
  return response.data;
};

export const createFinanceEntry = async (payload) => {
  const response = await apiClient.post('/finance/entries', payload);
  return response.data;
};

export const updateFinanceEntry = async (entryId, payload) => {
  const response = await apiClient.put(`/finance/entries/${entryId}`, payload);
  return response.data;
};

export const deleteFinanceEntry = async (entryId) => {
  const response = await apiClient.delete(`/finance/entries/${entryId}`);
  return response.data;
};

export const getFinanceCategories = async () => {
  const response = await apiClient.get('/finance/categories');
  return response.data;
};

export const createFinanceCategory = async (payload) => {
  const response = await apiClient.post('/finance/categories', payload);
  return response.data;
};

export const archiveFinanceCategory = async (categoryId) => {
  const response = await apiClient.post(`/finance/categories/${categoryId}/archive`);
  return response.data;
};

export const getFinanceStats = async () => {
  const response = await apiClient.get('/finance/stats');
  return response.data;
};

export const previewFinanceStatement = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post('/finance/statements/preview', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response.data;
};

export const confirmFinanceStatement = async (statementId, payload = {}) => {
  const response = await apiClient.post(`/finance/statements/${statementId}/confirm`, payload);
  return response.data;
};

export const getFinanceChatHistory = async (sessionId) => {
  const response = await apiClient.get(`/finance/chat/history?session_id=${sessionId}`);
  return response.data;
};

export const financeChatStream = async (sessionId, message, onChunk) => {
  const response = await fetch(`${API}/finance/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ session_id: sessionId, message })
  });
  if (!response.ok) {
    throw new Error('Stream error');
  }
  if (!response.body) {
    throw new Error('No stream');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    parts.forEach((line) => {
      if (line.startsWith('data: ')) {
        const data = line.replace('data: ', '').trim();
        if (data && data !== '[DONE]') {
          onChunk(data);
        }
      }
    });
  }
};

// Work types & rates (dynamic catalog)
export const getWorkTypesAdmin = async (params = {}) => {
  const response = await apiClient.get('/portal/work-types', { params });
  return response.data;
};

export const getWorkTypeUnits = async () => {
  const response = await apiClient.get('/portal/work-types/units');
  return response.data;
};

export const createWorkType = async (payload) => {
  const response = await apiClient.post('/portal/work-types', payload);
  return response.data;
};

export const updateWorkType = async (key, payload) => {
  const response = await apiClient.put(`/portal/work-types/${key}`, payload);
  return response.data;
};

export const deleteWorkType = async (key) => {
  const response = await apiClient.delete(`/portal/work-types/${key}`);
  return response.data;
};

export const addWorkTypeRate = async (key, payload) => {
  const response = await apiClient.post(`/portal/work-types/${key}/rates`, payload);
  return response.data;
};

export const deleteWorkTypeRate = async (key, rateId) => {
  const response = await apiClient.delete(`/portal/work-types/${key}/rates/${rateId}`);
  return response.data;
};

export const getEmployeeRates = async (params = {}) => {
  const response = await apiClient.get('/portal/employee-rates', { params });
  return response.data;
};

export const createEmployeeRate = async (payload) => {
  const response = await apiClient.post('/portal/employee-rates', payload);
  return response.data;
};

export const deleteEmployeeRate = async (rateId) => {
  const response = await apiClient.delete(`/portal/employee-rates/${rateId}`);
  return response.data;
};

export const getPriceSettings = async () => {
  const response = await apiClient.get('/portal/price-settings');
  return response.data;
};

export const addPriceValue = async (key, payload) => {
  const response = await apiClient.post(`/portal/price-settings/${key}`, payload);
  return response.data;
};

export const deletePriceValue = async (key, itemId) => {
  const response = await apiClient.delete(`/portal/price-settings/${key}/history/${itemId}`);
  return response.data;
};

export const resolveRate = async (params) => {
  const response = await apiClient.get('/portal/rates/resolve', { params });
  return response.data;
};
